import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
    Box,
    IconButton,
    Tooltip,
    Button,
    Typography,
    Paper,
    Stack,
    Divider,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import {
    PanTool as PanToolIcon,
    Create as CreateIcon,
    AutoFixNormal as EraserIcon,
    FiberManualRecord as DotIcon,
    Undo as UndoIcon,
    Redo as RedoIcon,
    Delete as ClearIcon,
    CropFree as SquareIcon,
    Save as SaveIcon,
    RestartAlt as ResetIcon,
} from '@mui/icons-material';

const BodyChart = ({ value, onChange, imageUrl: initialImageUrl, onSaveImage }) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);

    const [tool, setTool] = useState('dot');
    const [color, setColor] = useState('#00BCD4');
    const [dotSize, setDotSize] = useState(5);
    const [isDrawing, setIsDrawing] = useState(false);
    const [history, setHistory] = useState([]);
    const [redoStack, setRedoStack] = useState([]);
    const defaultImageUrl = '/user-forms/doctor-assessment/protocol-charts/body-chart.png';
    const [imageUrl, setImageUrl] = useState(initialImageUrl || defaultImageUrl);
    const [imgElement, setImgElement] = useState(null);
    const [imageSize, setImageSize] = useState('medium');

    const quickColors = ['#F44336', '#00BCD4', '#000000', '#4CAF50', '#FFEB3B'];

    // Parse initial value
    useEffect(() => {
        if (!value) return;
        try {
            const data = JSON.parse(value);
            if (data.history) setHistory(data.history);
            if (data.base64Image) setImageUrl(data.base64Image);
            else if (data.imageUrl) setImageUrl(data.imageUrl);
            if (data.imageSize) setImageSize(data.imageSize);
        } catch (e) {
            console.error("Failed to parse body chart data", e);
        }
    }, [value]);

    useEffect(() => {
        if (initialImageUrl) {
            setImageUrl(initialImageUrl);
            return;
        }
        if (!value) {
            setImageUrl(defaultImageUrl);
        }
    }, [initialImageUrl, value]);

    // Pre-load image to avoid flickering
    useEffect(() => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = imageUrl;
        img.onload = () => {
            setImgElement(img);
        };
    }, [imageUrl]);

    const saveToParent = useCallback((newHistory, currentImageUrl = imageUrl, base64) => {
        const payload = {
            history: newHistory,
            imageUrl: currentImageUrl,
            imageSize
        };
        if (base64 !== undefined) {
            payload.base64Image = base64;
        }
        const data = JSON.stringify(payload);
        onChange(data);
    }, [imageUrl, imageSize, onChange]);

    const redrawAll = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas || !imgElement) return;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const aspect = imgElement.width / imgElement.height;
        let drawWidth = canvas.width;
        let drawHeight = canvas.width / aspect;

        if (drawHeight > canvas.height) {
            drawHeight = canvas.height;
            drawWidth = canvas.height * aspect;
        }

        const x = (canvas.width - drawWidth) / 2;
        const y = (canvas.height - drawHeight) / 2;

        ctx.drawImage(imgElement, x, y, drawWidth, drawHeight);

        history.forEach(action => {
            if (action.type === 'line') {
                ctx.beginPath();
                ctx.strokeStyle = action.color;
                ctx.lineWidth = action.width || 2;
                ctx.lineCap = 'round';
                ctx.lineJoin = 'round';
                action.points.forEach((p, i) => {
                    if (i === 0) ctx.moveTo(p.x, p.y);
                    else ctx.lineTo(p.x, p.y);
                });
                ctx.stroke();
            } else if (action.type === 'dot') {
                ctx.beginPath();
                ctx.fillStyle = action.color;
                ctx.arc(action.pos.x, action.pos.y, action.size, 0, Math.PI * 2);
                ctx.fill();
            } else if (action.type === 'square') {
                ctx.fillStyle = action.color;
                ctx.fillRect(action.pos.x - action.size / 2, action.pos.y - action.size / 2, action.size, action.size);
            }
        });
    }, [history, imgElement]);

    useEffect(() => {
        redrawAll();
    }, [redrawAll]);

    const getPos = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        return {
            x: (e.clientX - rect.left) * scaleX,
            y: (e.clientY - rect.top) * scaleY
        };
    };

    const handleMouseDown = (e) => {
        const pos = getPos(e);

        if (tool === 'eraser') {
            const eraserRadius = 25;
            const newHistory = history.filter(action => {
                if (action.type === 'dot' || action.type === 'square') {
                    const dist = Math.sqrt(Math.pow(action.pos.x - pos.x, 2) + Math.pow(action.pos.y - pos.y, 2));
                    return dist > eraserRadius;
                } else if (action.type === 'line') {
                    return !action.points.some(p => {
                        const dist = Math.sqrt(Math.pow(p.x - pos.x, 2) + Math.pow(p.y - pos.y, 2));
                        return dist <= eraserRadius;
                    });
                }
                return true;
            });

            if (newHistory.length !== history.length) {
                setHistory(newHistory);
                saveToParent(newHistory);
            }
            return;
        }

        setIsDrawing(true);
        if (tool === 'pencil') {
            const newAction = { type: 'line', points: [pos], color, width: 2 };
            setHistory(prev => [...prev, newAction]);
        } else if (tool === 'dot') {
            const newAction = { type: 'dot', pos, color, size: dotSize };
            const newHistory = [...history, newAction];
            setHistory(newHistory);
            saveToParent(newHistory);
        } else if (tool === 'square') {
            const newAction = { type: 'square', pos, color, size: dotSize * 2 };
            const newHistory = [...history, newAction];
            setHistory(newHistory);
            saveToParent(newHistory);
        }
        setRedoStack([]);
    };

    const handleMouseMove = (e) => {
        if (!isDrawing || tool !== 'pencil') return;

        const pos = getPos(e);
        setHistory(prev => {
            const lastAction = prev[prev.length - 1];
            if (lastAction && lastAction.type === 'line') {
                const newPoints = [...lastAction.points, pos];
                const newHistory = [...prev.slice(0, -1), { ...lastAction, points: newPoints }];
                return newHistory;
            }
            return prev;
        });
    };

    const handleMouseUp = () => {
        if (isDrawing && tool === 'pencil') {
            saveToParent(history);
        }
        setIsDrawing(false);
    };

    const handleUndo = () => {
        if (history.length === 0) return;
        const lastAction = history[history.length - 1];
        const newHistory = history.slice(0, -1);
        setHistory(newHistory);
        setRedoStack(prev => [...prev, lastAction]);
        saveToParent(newHistory);
    };

    const handleRedo = () => {
        if (redoStack.length === 0) return;
        const action = redoStack[redoStack.length - 1];
        const newRedoStack = redoStack.slice(0, -1);
        const newHistory = [...history, action];
        setHistory(newHistory);
        setRedoStack(newRedoStack);
        saveToParent(newHistory);
    };

    const handleClear = () => {
        if (window.confirm("Are you sure you want to clear all markings?")) {
            setHistory([]);
            setRedoStack([]);
            saveToParent([]);
        }
    };

    const handleToolChange = (event, newTool) => {
        if (newTool !== null) {
            setTool(newTool);
        }
    };

    const handleSaveImage = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        // Ensure all drawings are rendered before capturing
        redrawAll();

        const base64 = canvas.toDataURL('image/png');
        const payload = JSON.stringify({
            history,
            imageUrl,
            imageSize,
            base64Image: base64
        });
        onChange(payload);
        if (onSaveImage) {
            onSaveImage(payload);
        }
        alert("Body chart image captured and saved!");
    };

    const handleResetImage = () => {
        if (window.confirm("Are you sure you want to reset the body chart? This will clear all markings and restore the initial image.")) {
            const resetUrl = defaultImageUrl;
            setHistory([]);
            setRedoStack([]);
            setImageUrl(resetUrl);
            onChange(JSON.stringify({
                history: [],
                imageUrl: resetUrl,
                imageSize,
                base64Image: null
            }));
        }
    };

    return (
        <Paper elevation={1} sx={{ p: 4, my: 4, borderRadius: 2 }}>
            <Typography variant="h3" mb={6}>Body Chart</Typography>

            <Stack direction="row" spacing={3}>
                <Paper
                    variant="outlined"
                    sx={{
                        p: 1.5, display: 'flex', flexDirection: 'column', gap: 2,
                        backgroundColor: '#f8f9fa', borderRadius: 2, width: '60px', alignItems: 'center'
                    }}
                >
                    <ToggleButtonGroup
                        orientation="vertical" value={tool} exclusive onChange={handleToolChange}
                        size="small" sx={{ gap: 1, border: 'none', '& .MuiToggleButton-root': { border: 'none', borderRadius: 1 } }}
                    >
                        <ToggleButton value="select"><Tooltip title="Select" placement="right"><PanToolIcon /></Tooltip></ToggleButton>
                        <ToggleButton value="pencil"><Tooltip title="Pencil" placement="right"><CreateIcon /></Tooltip></ToggleButton>
                        <ToggleButton value="eraser"><Tooltip title="Eraser" placement="right"><EraserIcon /></Tooltip></ToggleButton>
                    </ToggleButtonGroup>
                    <Divider sx={{ width: '80%' }} />
                    <Stack spacing={1} alignItems="center">
                        <IconButton size="small" color={tool === 'dot' && dotSize === 3 ? 'primary' : 'default'} onClick={() => { setTool('dot'); setDotSize(3); }}>
                            <DotIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                        <IconButton size="small" color={tool === 'dot' && dotSize === 6 ? 'primary' : 'default'} onClick={() => { setTool('dot'); setDotSize(6); }}>
                            <DotIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                        <IconButton size="small" color={tool === 'dot' && dotSize === 10 ? 'primary' : 'default'} onClick={() => { setTool('dot'); setDotSize(10); }}>
                            <DotIcon sx={{ fontSize: 28 }} />
                        </IconButton>
                        <IconButton size="small" color={tool === 'square' ? 'primary' : 'default'} onClick={() => { setTool('square'); }}>
                            <SquareIcon sx={{ fontSize: 24 }} />
                        </IconButton>
                    </Stack>
                    <Divider sx={{ width: '80%' }} />
                    <Stack spacing={1} alignItems="center">
                        {quickColors.map(c => (
                            <Box
                                key={c} onClick={() => setColor(c)}
                                sx={{
                                    width: '24px', height: '24px', borderRadius: '50%', backgroundColor: c,
                                    cursor: 'pointer', border: color === c ? '2px solid #3f51b5' : '1px solid #ddd'
                                }}
                            />
                        ))}
                    </Stack>
                    <Divider sx={{ width: '80%' }} />
                    <Stack spacing={1}>
                        <IconButton size="small" onClick={handleUndo} disabled={history.length === 0}><UndoIcon fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={handleRedo} disabled={redoStack.length === 0}><RedoIcon fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={handleClear} color="error"><ClearIcon fontSize="small" /></IconButton>
                    </Stack>
                </Paper>

                <Box sx={{ flexGrow: 1, position: 'relative' }}>
                    <Box
                        ref={containerRef}
                        sx={{
                            border: '1px solid #e0e0e0', borderRadius: 2, position: 'relative',
                            minHeight: '600px', backgroundColor: '#ffffff', overflow: 'hidden',
                            display: 'flex', justifyContent: 'center', alignItems: 'center'
                        }}
                    >
                        <canvas
                            ref={canvasRef}
                            width={1200}
                            height={900}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                            style={{
                                cursor: tool === 'pencil' ? 'crosshair' : tool === 'eraser' ? 'not-allowed' : 'default',
                                maxWidth: '100%', maxHeight: '100%', objectFit: 'contain'
                            }}
                        />
                    </Box>

                    <Stack direction="row" spacing={2} sx={{ mt: 3, alignItems: 'center' }}>
                        <Button variant="contained" color="primary" startIcon={<SaveIcon />} onClick={handleSaveImage}>
                            Save Image
                        </Button>
                        <Button variant="outlined" color="error" startIcon={<ResetIcon />} onClick={handleResetImage}>
                            Reset Image
                        </Button>
                    </Stack>
                </Box>
            </Stack>
        </Paper>
    );
};

export default BodyChart;
